import React,{useState,useMemo,useEffect,useCallback,useRef} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {format,addDays,getDay,differenceInDays,parseISO} from 'date-fns';
import {supabase} from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import TaskCard from './TaskCard';

const DAYS_OF_WEEK=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const ScheduleShifter=({tasks,onUpdate,planStartDate})=> {
  const [isShifting,setIsShifting]=useState(false);
  const [showPicker,setShowPicker]=useState(false);
  const [newStartDate,setNewStartDate]=useState(format(new Date(),'yyyy-MM-dd'));

  const incompleteTasks=useMemo(()=> {
    return tasks
      .filter(t=> !t.is_completed)
      .sort((a,b)=> {
        if (a.week_number !==b.week_number) return a.week_number - b.week_number;
        return a.day_number - b.day_number;
      });
  },[tasks]);

  const handleShift=async ()=> {
    if (incompleteTasks.length===0 || !planStartDate) return;
    setIsShifting(true);
    try {
      const firstIncomplete=incompleteTasks[0];
      const daysIntoPlan=((firstIncomplete.week_number - 1) * 7) + (firstIncomplete.day_number - 1);
      
      const [ny,nm,nd]=newStartDate.split('-').map(Number);
      const targetDate=new Date(ny,nm - 1,nd);
      const calculatedNewPlanStart=addDays(targetDate,-daysIntoPlan);
      const newPlanStartStr=format(calculatedNewPlanStart,'yyyy-MM-dd');

      const {data: plan}=await supabase
        .from('training_tasks')
        .select('plan_id')
        .eq('id',firstIncomplete.id)
        .single();

      if (plan) {
        await supabase
          .from('training_plans')
          .update({start_date: newPlanStartStr})
          .eq('id',plan.plan_id);
      }

      const {data: allTasks}=await supabase
        .from('training_tasks')
        .select('id,week_number,day_number')
        .eq('plan_id',plan.plan_id);

      if (allTasks) {
        const updates=allTasks.map(t=> {
          const daysToAdd=((t.week_number - 1) * 7) + (t.day_number - 1);
          const newDate=format(addDays(calculatedNewPlanStart,daysToAdd),'yyyy-MM-dd');
          return supabase.from('training_tasks').update({task_date: newDate}).eq('id',t.id);
        });
        await Promise.all(updates);
      }

      setShowPicker(false);
      onUpdate();
    } catch (err) {
      console.error('Shift failed:',err);
      alert('Failed to update schedule.');
    } finally {
      setIsShifting(false);
    }
  };

  if (incompleteTasks.length===0) return null;

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-[2rem] p-6 mt-8 shadow-sm">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-100">
            <SafeIcon name="Calendar" className="w-6 h-6" />
          </div>
          <div>
            <h4 className="font-black text-indigo-900 text-lg">Schedule Assistant</h4>
            <p className="text-xs text-indigo-600 font-bold uppercase tracking-widest">
              {incompleteTasks.length} tasks remaining
            </p>
          </div>
        </div>

        {!showPicker ? (
          <button 
            onClick={()=> setShowPicker(true)}
            className="bg-white text-indigo-600 border-2 border-indigo-100 px-6 py-3 rounded-2xl text-xs font-black hover:bg-indigo-100 transition-all active:scale-95"
          >
            Shift Remaining Schedule
          </button>
        ) : (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4">
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1 mb-1">New Resume Date</span>
              <input 
                type="date" 
                value={newStartDate}
                onChange={(e)=> setNewStartDate(e.target.value)}
                className="bg-white border-2 border-indigo-200 rounded-xl px-4 py-2 text-sm font-black text-indigo-900 outline-none focus:border-indigo-500"
              />
            </div>
            <div className="flex gap-2 self-end">
              <button onClick={handleShift} disabled={isShifting} className="bg-indigo-600 text-white p-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-lg shadow-indigo-100">
                {isShifting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon name="Check" />}
              </button>
              <button onClick={()=> setShowPicker(false)} className="bg-white border-2 border-gray-100 text-gray-400 p-3 rounded-xl hover:bg-gray-50">
                <SafeIcon name="X" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TaskListView=({tasks,onUpdate,planStartDate})=> {
  const [isRescheduling,setIsRescheduling]=useState(null);
  const [expandedWeek,setExpandedWeek]=useState(null);
  const hasInitialized=useRef(false);

  const getDayLabel=useCallback((week,day)=> {
    if (!planStartDate) return `Day ${day}`;
    try {
      const [year,month,d]=planStartDate.split('-').map(Number);
      const baseDate=new Date(year,month - 1,d);
      const targetDate=addDays(baseDate,((week - 1) * 7) + (day - 1));
      return `${DAYS_OF_WEEK[getDay(targetDate)]}`;
    } catch (e) {
      return `Day ${day}`;
    }
  },[planStartDate]);

  const calculateDateFromPosition=useCallback((week,day)=> {
    if (!planStartDate) return null;
    try {
      const [year,month,day_part]=planStartDate.split('-').map(Number);
      const baseDate=new Date(year,month - 1,day_part);
      const daysToAdd=((week || 1) - 1) * 7 + ((day || 1) - 1);
      return format(addDays(baseDate,daysToAdd),'yyyy-MM-dd');
    } catch (e) {
      return null;
    }
  },[planStartDate]);

  const handleRescheduleRestDay=async (taskId,newDayNumber)=> {
    const task=tasks.find(t=> t.id===taskId);
    if (!task || !planStartDate) return;
    setIsRescheduling(taskId);
    try {
      const weekNumber=task.week_number;
      const targetTask=tasks.find(t=> t.week_number===weekNumber && t.day_number===newDayNumber);
      const updates=[];
      if (targetTask) {
        updates.push({id: targetTask.id,day_number: task.day_number,task_date: calculateDateFromPosition(weekNumber,task.day_number)});
      }
      updates.push({id: taskId,day_number: newDayNumber,task_date: calculateDateFromPosition(weekNumber,newDayNumber)});
      for (const update of updates) {
        const {id,...data}=update;
        await supabase.from('training_tasks').update(data).eq('id',id);
      }
      const {data: allTasks}=await supabase .from('training_tasks') .select('id,week_number,day_number') .eq('plan_id',task.plan_id);
      if (allTasks) {
        const resyncPromises=allTasks.map(t=> {
          const correctDate=calculateDateFromPosition(t.week_number,t.day_number);
          return supabase.from('training_tasks').update({task_date: correctDate}).eq('id',t.id);
        });
        await Promise.all(resyncPromises);
      }
      onUpdate();
    } catch (err) {
      console.error('Rescheduling failed:',err);
    } finally {
      setIsRescheduling(null);
    }
  };

  const tasksByWeek=useMemo(()=> {
    const grouped={};
    tasks.forEach(task=> {
      const week=task.week_number || 1;
      if (!grouped[week]) grouped[week]=[];
      grouped[week].push(task);
    });
    Object.keys(grouped).forEach(week=> {
      grouped[week].sort((a,b)=> a.day_number - b.day_number);
    });
    return grouped;
  },[tasks]);

  const initialWeek=useMemo(()=> {
    const sortedWeekNums=Object.keys(tasksByWeek).map(Number).sort((a,b)=> a - b);
    if (sortedWeekNums.length===0) return 1;
    for (const weekNum of sortedWeekNums) {
      if (tasksByWeek[weekNum].some(t=> !t.is_completed)) return weekNum;
    }
    return sortedWeekNums[sortedWeekNums.length - 1];
  },[tasksByWeek]);

  useEffect(()=> {
    if (!hasInitialized.current && Object.keys(tasksByWeek).length > 0) {
      setExpandedWeek(initialWeek);
      hasInitialized.current=true;
    }
  },[initialWeek,tasksByWeek]);

  return (
    <div className="space-y-4">
      {Object.keys(tasksByWeek).sort((a,b)=> Number(a) - Number(b)).map(weekNum=> {
        const isExpanded=Number(weekNum)===expandedWeek;
        const weekTasks=tasksByWeek[weekNum];
        const completedCount=weekTasks.filter(t=> t.is_completed).length;
        const isFullyCompleted=completedCount===weekTasks.length;

        return (
          <div 
            key={weekNum} 
            className={`bg-white rounded-xl shadow-sm border transition-all ${isExpanded ? 'border-indigo-200 ring-1 ring-indigo-50 z-20' : 'border-gray-200 z-10'}`}
          >
            <button 
              onClick={()=> setExpandedWeek(isExpanded ? null : Number(weekNum))}
              className={`w-full flex items-center justify-between p-4 transition-colors ${isExpanded ? 'bg-indigo-50/30' : 'bg-gray-50 hover:bg-gray-100'}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${isExpanded ? 'bg-indigo-600 text-white' : isFullyCompleted ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-600'}`}>
                  {isFullyCompleted && !isExpanded ? <SafeIcon name="Check" className="w-4 h-4" /> : weekNum}
                </div>
                <div className="text-left">
                  <h3 className={`font-bold ${isExpanded ? 'text-indigo-900' : 'text-gray-800'}`}>Week {weekNum}</h3>
                  <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest">
                    {completedCount} / {weekTasks.length} Days Completed
                  </p>
                </div>
              </div>
              <SafeIcon name={isExpanded ? "ChevronUp" : "ChevronDown"} className={`w-5 h-5 ${isExpanded ? 'text-indigo-600' : 'text-gray-400'}`} />
            </button>

            <AnimatePresence>
              {isExpanded && (
                <motion.div 
                  initial={{height: 0,opacity: 0}}
                  animate={{height: 'auto',opacity: 1}}
                  exit={{height: 0,opacity: 0}}
                  className="p-2 sm:p-4 space-y-4 border-t border-gray-100 overflow-visible"
                >
                  {weekTasks.map(task=> (
                    <div key={task.id} className="relative">
                      <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gray-100 rounded-full" />
                      <div className="pl-2 sm:pl-4">
                        <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                          <SafeIcon name="Calendar" className="w-3 h-3" />
                          {getDayLabel(task.week_number,task.day_number)}
                        </div>
                        <TaskCard 
                          task={task} 
                          onUpdate={onUpdate} 
                          onRescheduleRestDay={handleRescheduleRestDay}
                          isRescheduling={isRescheduling===task.id}
                          dayLabel={getDayLabel(task.week_number,task.day_number)}
                          otherDaysInWeek={[1,2,3,4,5,6,7].map(d=> ({dayNumber: d,label: getDayLabel(task.week_number,d)}))}
                        />
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
      <ScheduleShifter tasks={tasks} onUpdate={onUpdate} planStartDate={planStartDate} />
    </div>
  );
};

export default TaskListView;